import { GetServerSideProps } from 'next'
import { MerchScreen } from 'app/features/merch/screen'
import { dehydrate, QueryClient } from 'react-query'
import { makeQuery as makeSingleQuery } from 'app/hooks/use-query'
import {
  buildRoadmapCollectiblesVariables,
  buildPurchasableCollectiblesVariables,
} from 'app/queries/collectibles/list'
import {
  createUserCollectiblesQuery,
  mapToUserCollectibles,
} from 'app/queries/collectibles/user-collectibles'
import {
  createMerchPageQuery,
  mapToMerchPageContent,
} from 'app/queries/globals/merch'
import { collectiblesPrefetcher } from '../../lib/collectibles-prefetcher'
import { withAuthedPage } from '../../lib/session'
import { verifyUser } from 'lib/verify-user'

export default MerchScreen

const resolveRoadmapId = (roadmap: unknown): string | undefined => {
  if (typeof roadmap === 'string') return roadmap
  if (roadmap && typeof roadmap === 'object' && 'id' in roadmap) {
    const id = (roadmap as { id?: unknown }).id
    return typeof id === 'string' ? id : undefined
  }

  return undefined
}

const resolveRoadmapYear = (roadmap: unknown): number | undefined => {
  if (roadmap && typeof roadmap === 'object' && 'year' in roadmap) {
    const year = (roadmap as { year?: unknown }).year
    return typeof year === 'number' ? year : undefined
  }

  return undefined
}

const resolveYearFromUserCollectibles = (
  collectibles?: { availableFrom?: string | null; quarter?: string }[],
): number | undefined => {
  if (!collectibles?.length) return undefined

  const fromAvailableFrom = collectibles
    .map((item) => item?.availableFrom)
    .find((value) => typeof value === 'string' && value.length > 0)

  if (typeof fromAvailableFrom === 'string') {
    const parsed = new Date(fromAvailableFrom)
    const year = parsed.getUTCFullYear()
    if (!Number.isNaN(parsed.getTime()) && Number.isFinite(year)) {
      return year
    }
  }

  const fromQuarter = collectibles
    .map((item) => item?.quarter)
    .find((value) => typeof value === 'string' && /\b(20\d{2})\b/.test(value))

  if (typeof fromQuarter === 'string') {
    const match = fromQuarter.match(/\b(20\d{2})\b/)
    const year = match?.[1] ? Number(match[1]) : NaN
    if (Number.isFinite(year)) {
      return year
    }
  }

  return undefined
}

export const getServerSideProps: GetServerSideProps = withAuthedPage(
  async (context) => {
    const store = context.req?.userStore
    const redirect = verifyUser(store)
    if (redirect) return redirect

    const queryClient = new QueryClient()
    const merchPageQuery = createMerchPageQuery()

    let data
    try {
      const authedMerchData = await makeSingleQuery(
        merchPageQuery,
        {},
        mapToMerchPageContent,
        store,
      )
      let mergedMerchData = authedMerchData

      if (!resolveRoadmapId(authedMerchData?.collectiblesRoadmap)) {
        const publicMerchData = await makeSingleQuery(
          merchPageQuery,
          {},
          mapToMerchPageContent,
        )

        if (publicMerchData?.collectiblesRoadmap) {
          mergedMerchData = {
            ...(authedMerchData || publicMerchData || {}),
            collectiblesRoadmap:
              publicMerchData.collectiblesRoadmap ??
              authedMerchData?.collectiblesRoadmap,
          }
        }
      }

      data = await queryClient.fetchQuery(
        ['query', merchPageQuery, {}],
        async () => {
          return mergedMerchData
        },
      )
    } catch (e) {
      console.log(e)
      throw e
    }

    const collectiblesRoadmapId = resolveRoadmapId(data?.collectiblesRoadmap)
    const roadmapYear = resolveRoadmapYear(data?.collectiblesRoadmap)
    let resolvedRoadmapId = collectiblesRoadmapId
    let resolvedRoadmapYear = roadmapYear

    if (!resolvedRoadmapId) {
      const userCollectiblesQuery = createUserCollectiblesQuery()
      const userCollectibles = await makeSingleQuery(
        userCollectiblesQuery,
        {},
        mapToUserCollectibles,
        store,
      )

      await queryClient.fetchQuery(
        ['query', userCollectiblesQuery, {}],
        async () => userCollectibles,
      )

      const activeRoadmapYear = resolveYearFromUserCollectibles(
        userCollectibles?.collectibles,
      )

      if (typeof activeRoadmapYear === 'number') {
        resolvedRoadmapYear = activeRoadmapYear
      }
    }

    const collectiblesYear = resolvedRoadmapYear ?? new Date().getUTCFullYear()

    await Promise.all([
      collectiblesPrefetcher(
        queryClient,
        buildRoadmapCollectiblesVariables(
          resolvedRoadmapId,
          collectiblesYear,
        ),
      ),
      collectiblesPrefetcher(queryClient, buildPurchasableCollectiblesVariables()),
    ])

    const dehydratedState = dehydrate(queryClient)
    return {
      props: {
        dehydratedState,
      },
    }
  },
)
